/*
 * Notion exports a database as a ZIP holding one or two CSVs ("Name abc123.csv" and
 * "Name abc123_all.csv", the latter with every row). This pulls the CSV out in the browser so
 * traders can drop the export straight in. Stored and deflated entries are supported.
 */
export async function csvFromUpload(file: File): Promise<File> {
  if (!/\.zip$/i.test(file.name) && file.type !== 'application/zip') return file;
  const buf = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(buf.buffer);
  // End of central directory: scan back for its signature
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('That ZIP looks damaged — try exporting again');
  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const entries: { name: string; method: number; size: number; offset: number }[] = [];
  for (let n = 0; n < count; n++) {
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true), extraLen = view.getUint16(p + 30, true), commentLen = view.getUint16(p + 32, true);
    const offset = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(buf.subarray(p + 46, p + 46 + nameLen));
    entries.push({ name, method, size, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  const csvs = entries.filter(e => /\.csv$/i.test(e.name) && !e.name.startsWith('__MACOSX'));
  if (!csvs.length) throw new Error('No CSV inside that ZIP — export the Master Trade Log as CSV');
  // Prefer the "_all" file (every row, not just the visible view)
  const pick = csvs.find(e => /_all\.csv$/i.test(e.name)) ?? csvs[0];
  const local = pick.offset;
  const dataStart = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
  const raw = buf.subarray(dataStart, dataStart + pick.size);
  let bytes: Uint8Array<ArrayBuffer>;
  if (pick.method === 0) bytes = raw.slice();
  else if (pick.method === 8) {
    const stream = new Blob([raw.slice()]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  } else throw new Error('Unsupported ZIP compression — unzip it and drop the CSV instead');
  return new File([bytes], pick.name.split('/').pop() || 'export.csv', { type: 'text/csv' });
}
